import React from 'react';
import { Card, Badge, Button } from '@/components/ui';
import { CheckInBadge } from './CheckInBadge';
import { User, MapPin, Timer } from 'lucide-react';
import type { AttendanceRecord, AttendanceStatus } from '../types/attendance.types';

interface AttendanceCardProps {
  record: AttendanceRecord;
  onCheckOut?: (id: string) => void;
  isCheckingOut?: boolean;
  currentUserId?: string;
}

const statusMap: Record<AttendanceStatus, { label: string; color: string }> = {
  present: { label: 'Đang làm', color: 'bg-emerald-100 text-emerald-600' },
  absent: { label: 'Vắng mặt', color: 'bg-red-100 text-red-600' },
  late: { label: 'Đi muộn', color: 'bg-amber-100 text-amber-600' },
  leave: { label: 'Nghỉ phép', color: 'bg-blue-100 text-blue-600' },
  half_day: { label: 'Nửa ngày', color: 'bg-indigo-100 text-indigo-600' },
  on_duty: { label: 'Đã vào ca', color: 'bg-emerald-100 text-emerald-600' },
  off_duty: { label: 'Đã tan ca', color: 'bg-slate-100 text-slate-500' },
};

export const AttendanceCard: React.FC<AttendanceCardProps> = ({
  record,
  onCheckOut,
  isCheckingOut,
  currentUserId
}) => {
  const status = statusMap[record.status_code] || statusMap.off_duty;
  const isCurrentUser = record.user_id === currentUserId;

  const formatTime = (isoString?: string) => {
    if (!isoString) return undefined;
    return new Date(isoString).toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <Card className="group border-none shadow-sm hover:shadow-xl transition-all duration-500 bg-white rounded-[24px] overflow-hidden p-0 ring-1 ring-slate-100">
      <div className="p-5">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden shadow-sm">
                {record.user?.avatar_url ? (
                  <img src={record.user.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-6 h-6 text-slate-300" />
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white bg-emerald-500 shadow-sm" />
            </div>
            <div>
              <h4 className="font-black text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight">
                {record.user?.full_name}
              </h4>
              <p className="text-[10px] text-slate-400 font-black  tracking-widest">
                {record.team?.name || 'Đội vãng lai'}
              </p>
            </div>
          </div>
          <Badge className={`${status.color} border-none font-black text-[9px]  tracking-widest px-2.5 py-1 rounded-full shadow-sm`}>
            {status.label}
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-3 mb-5">
          <CheckInBadge
            type="in"
            time={formatTime(record.check_in_time)}
            location={record.zone?.zone_name || 'Cổng chính'}
          />
          {record.check_out_time ? (
            <CheckInBadge
              type="out"
              time={formatTime(record.check_out_time)}
              location="Lối ra A"
            />
          ) : (
            <div className="p-3 rounded-2xl border border-dashed border-slate-200 flex items-center justify-center bg-slate-50/30">
              <span className="text-[10px] font-bold text-slate-400  tracking-widest italic">Chưa kết thúc ca</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-slate-50">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Timer className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-black text-slate-700">{record.working_hours?.toFixed(1) || 0}h</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-[10px] font-bold text-slate-500 truncate max-w-[100px]">
                {record.project?.name || 'Dự án hiện tại'}
              </span>
            </div>
          </div>

          {isCurrentUser && !record.check_out_time && onCheckOut && (
            <Button
              size="sm"
              variant="primary"
              className="h-8 rounded-xl px-4 text-[10px] font-black  tracking-widest shadow-lg shadow-blue-500/20"
              onClick={() => onCheckOut(record.id)}
              isLoading={isCheckingOut}
            >
              Check-out
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
